import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import {
    Col,
    Dropdown,
    DropdownButton,
    Row,
} from 'react-bootstrap';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import {
    AuthedLayout,
} from '../../../components';
import { useDownloadChart, useSemGrade } from '../../../hook';
import { WHChartStatistic } from '../../../model';
import apis from '../../../apis';
import useApexBarChart, { ChartState } from '../../../hook/useApexBarChart';
import { toast } from 'react-toastify';

const mapState = (app: ApplicationState) => ({ ...app.auth });
const mapDispatch = ErrorDispatches;

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;

const whWeightChart: FunctionComponent<Props> = ({
    user,
    catchErrorForModal,
}) => {
    const { yearSem, element: semGradeElement } = useSemGrade();
    const [students, setStudents] = useState<WHChartStatistic[]>([]);
    const chartRef = useRef(null);
    useEffect(() => {
        if (yearSem) {
            toast
                .promise(
                    apis
                        .getWeightChart(yearSem.year, yearSem.sem)
                        .then(r => {
                            setStudents(
                                r.map(({ ...s }) => ({
                                    ...s,
                                }))
                            )
                        }),
                    {
                        pending: '資料載入中......',
                        success: '資料載入成功！',
                        error: '資料載入失敗！請查看錯誤資訊。',
                    }
                )

        }
    }, [yearSem?.year, yearSem?.sem]);

    const grades = Array.from(new Set(students.map(s => s.grade))).sort((a, b) => a - b);

    const maleData = grades.map(g => {
        const m = students.find(s => s.grade === g && s.sex === 1);
        return m ? Number(m.count.toFixed(1)) : 0;
    });

    const femaleData = grades.map(g => {
        const f = students.find(s => s.grade === g && s.sex === 2);
        return f ? Number(f.count.toFixed(1)) : 0;
    });
    const state: ChartState = {
        options: {
            chart: { id: 'heightchart' },
            xaxis: {
                title: { text: "年級" },
                categories: grades
            },
            yaxis: {
                title: { text: "體重(公斤)" },
            },
        },
        series: [
            {
                name: '男',
                data: maleData
            },
            {
                name: '女',
                data: femaleData
            }
        ],
        charttype: 'bar'
    };
    const { element: barchart } = useApexBarChart(state);
    const { downloadChart } = useDownloadChart({
        chartdata: state.series[0].data,
        yearSem,
        chartRef,
        title:
            `${user.schools[user.currentSchool].name}` +
            `${yearSem?.year}學年第${yearSem?.sem}學期_體重統計圖`,
    });

    const handlePrint = () => {
        const chartElement = chartRef.current as HTMLElement | null;

        if (!chartElement) return;

        const printWindow = window.open(
            '',
            '_blank',
            'width=1000,height=800'
        );

        if (!printWindow) {
            alert('瀏覽器阻擋了列印視窗，請允許彈出式視窗。');
            return;
        }

        const title =
            `${user.schools[user.currentSchool].name}` +
            `${yearSem?.year}學年第${yearSem?.sem}學期_體重統計圖`;

        printWindow.document.open();

        printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="zh-Hant">
        <head>
            <meta charset="UTF-8" />
            <title>${title}</title>

            <style>
                * {
                    box-sizing: border-box;
                }

                html,
                body {
                    margin: 0;
                    padding: 0;
                    font-family:
                        "Microsoft JhengHei",
                        "Noto Sans TC",
                        Arial,
                        sans-serif;
                    color: #000;
                }

                .print-page {
                    min-height: 190mm;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                h1 {
                    margin: 0 0 20px;
                    font-size: 20px;
                    text-align: center;
                }

                .chart-container {
                    width: 100%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }

                .chart-container svg {
                    max-width: 100% !important;
                    height: auto !important;
                }

               .footer {
    margin-top: auto;
    padding-top: 16px;
    font-size: 12px;
    display: flex;
    justify-content: space-between;
    width: 100%;
}

                @page {
                    size: A4 landscape;
                    margin: 10mm;
                }

                @media print {
                    body {
                        padding: 0;
                    }
                }
            </style>
        </head>

        <body>
            <div class="print-page">
                <h1>${title}</h1>

                <div class="chart-container">
                    ${chartElement.innerHTML}
                </div>

                <div class="footer">
  <span>承辦人:</span>
  <span>組長:</span>
  <span>主任:</span>
  <span>校長:</span>
</div>
            </div>

            <script>
                window.onload = function () {
                    window.focus();

                    setTimeout(function () {
                        window.print();
                    }, 300);
                };

                window.onafterprint = function () {
                    window.close();
                };
            </script>
        </body>
        </html>
    `);

        printWindow.document.close();
    };

    return (
        <AuthedLayout>
            <Row className="justify-content-between">
                <Col xs={3} className="mr-3">
                    {semGradeElement}
                </Col>
                <Col xs={2} className="mr-2">
                    <DropdownButton
                        id="wh-weight-chart-export"
                        title="匯出"
                        variant="success"
                        disabled={!students.length}
                    >
                        <Dropdown.Item
                            onClick={() => {
                                downloadChart();
                            }}
                        >
                            Excel 下載
                        </Dropdown.Item>

                        <Dropdown.Item onClick={handlePrint}>
                            列印
                        </Dropdown.Item>
                    </DropdownButton>
                </Col>
            </Row>
            <hr />
            <Row>
                <Col className="text-center">
                    {user.schools[user.currentSchool].name}{yearSem?.year}學年第{yearSem?.sem}學期_體重統計圖
                </Col>
            </Row>
            <Row className="justify-content-center">
                <div ref={chartRef}>
                    {barchart}
                </div>
            </Row>
        </AuthedLayout>
    );
};

export const WHWeightChart = connector(whWeightChart);
